// src/pages/ExperienceLetter.js
import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useReactToPrint } from "react-to-print";
import html2pdf from "html2pdf.js";
import { ArrowLeft, Calendar, Printer, Download } from "lucide-react";
import "../letterstyles/ExperienceLetter.css";

const ExperienceLetter = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const printRef = useRef(null);
  const user = state?.user;

  const superAdminId = Number(localStorage.getItem("adminuserId")) || 0;

  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);

  // Date management states
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Temporary states for modal form inputs
  const [tempFromDate, setTempFromDate] = useState("");
  const [tempToDate, setTempToDate] = useState("");

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: `${user?.fullName || "Employee"}_Experience_Letter`,
  });

  const handleDownloadPDF = () => {
    const element = printRef.current;
    if (!element) return;

    const opt = {
      margin: 0,
      filename: `${user?.fullName || "Employee"}_Experience_Letter.pdf`,
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: {
        scale: 2.5,
        useCORS: true,
        allowTaint: true,
        scrollY: 0,
        logging: false,
        letterRendering: true,
      },
      jsPDF: {
        unit: "mm",
        format: "a4",
        orientation: "portrait",
        compressPdf: true,
      },
      pagebreak: {
        mode: ["avoid-all", "css", "legacy"],
        avoid: [".el-doc-header", ".el-doc-sign-block", ".el-doc-footer"],
      },
    };

    html2pdf().set(opt).from(element).save();
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "N/A";
    return date
      .toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
      .replace(/ /g, "-");
  };

  const handleOpenModal = () => {
    setTempFromDate(fromDate);
    setTempToDate(toDate);
    setIsModalOpen(true);
  };

  const handleSaveDates = (e) => {
    e.preventDefault();
    setFromDate(tempFromDate);
    setToDate(tempToDate);
    setIsModalOpen(false);
  };

  useEffect(() => {
    if (!user) {
      alert("No user data found!");
      navigate("/");
    }

    const fetchCompany = async () => {
      try {
        setLoading(true);
        const response = await fetch(
          `https://payyug.in/api/get-company?superAdminId=${superAdminId}`
        );
        const data = await response.json();

        if (response.ok && data) {
          if (data.logo) {
            data.logoUrl = `https://payyug.in/${data.logo.replace(/^images\//, "")}`;
          }
          setCompany(data);
        } else {
          console.error("Failed to fetch company data");
        }
      } catch (err) {
        console.error("Error fetching company details:", err);
      } finally {
        setLoading(false);
      }
    };

    if (superAdminId) fetchCompany();
  }, [navigate, user, superAdminId]);

  if (!user) return null;
  if (loading) {
    return (
      <div className="el-doc-loading">
        <p>Loading company details...</p>
      </div>
    );
  }

  return (
    <div className="el-doc-wrapper">
      {/* Top Action Toolbar */}
      <div className="el-doc-action-bar">
        <button
          type="button"
          className="el-doc-btn el-doc-btn-back"
          onClick={() => navigate(-1)}
        >
          <ArrowLeft size={16} /> Back
        </button>

        <div className="el-doc-action-group">
          <button
            type="button"
            className="el-doc-btn el-doc-btn-date"
            onClick={handleOpenModal}
          >
            <Calendar size={16} /> Select Dates
          </button>
          <button
            type="button"
            className="el-doc-btn el-doc-btn-print"
            onClick={handlePrint}
          >
            <Printer size={16} /> Print
          </button>
          <button
            type="button"
            className="el-doc-btn el-doc-btn-download"
            onClick={handleDownloadPDF}
          >
            <Download size={16} /> Download
          </button>
        </div>
      </div>

      {/* Printable Sheet Container */}
      <div className="el-doc-sheet" ref={printRef}>
        {/* Header */}
        <div className="el-doc-header">
          <img
            src={company?.logoUrl || "/default-logo.png"}
            alt="Company Logo"
            className="el-doc-logo"
          />
          <div className="el-doc-company-info">
            <h2>{company?.name}</h2>
            <p><strong>CIN:</strong> {company?.cin || "N/A"}</p>
            <p><strong>Regt. Office:</strong> {company?.address || "N/A"}</p>
            <p><strong>Email:</strong> {company?.email || "N/A"}</p>
            <p><strong>Contact:</strong> {company?.contact || "N/A"}</p>
          </div>
        </div>

        {/* Title */}
        <div className="el-doc-title-wrapper">
          <h2>Experience Letter</h2>
        </div>

        {/* Body Content */}
        <div className="el-doc-body">
          <p className="el-doc-date">
            <strong>Date:</strong> {new Date().toLocaleDateString("en-GB")}
          </p>

          <p><strong>To,</strong></p>
          <p>{user.fullName || user.name}</p>
          <p>{user.branchName || user.branch}</p>

          <p className="el-doc-subject">
            <strong>Subject:</strong> Experience Certificate
          </p>
          <p className="el-doc-designation">
            <strong>Designation:</strong> {user?.roleName || user?.role || "Employee"}
          </p>

          <p>Dear {user.fullName || user.name},</p>

          <p>
            This is to certify that <strong>{user.fullName || user.name}</strong> was employed with{" "}
            <strong>{company?.name}</strong> as a <strong>{user.roleName || user.role || "Employee"}</strong> at
            our <strong>{user.branchName || user.branch}</strong> branch from{" "}
            <strong>{fromDate ? formatDate(fromDate) : "N/A"}</strong> to{" "}
            <strong>{toDate ? formatDate(toDate) : "N/A"}</strong>.
          </p>

          <p>
            During the tenure, {user.fullName || user.name} exhibited excellent performance,
            professionalism, and dedication to responsibilities. We appreciate the contributions and wish
            the best in future endeavors.
          </p>

          <p>We wish {user.fullName || user.name} all the success in future.</p>

          <div className="el-doc-sign-block">
            <p>Regards,</p>
            <br />
            <p>
              <strong>HR Manager</strong>
              <br />
              <br />
              {company?.name}
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="el-doc-footer">
          <hr />
          <p>{company?.name} | CIN: {company?.cin || "N/A"}</p>
          <p>Email: {company?.email || "N/A"} | {company?.contact || "N/A"}</p>
        </div>
      </div>

      {/* Date Configuration Overlay Modal */}
      {isModalOpen && (
        <div className="el-modal-backdrop" onClick={() => setIsModalOpen(false)}>
          <div className="el-modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Select Certificate Duration</h3>
            <form onSubmit={handleSaveDates} className="el-modal-form">
              <div className="el-form-group">
                <label htmlFor="elFromDate">From Date:</label>
                <input
                  id="elFromDate"
                  type="date"
                  value={tempFromDate}
                  onChange={(e) => setTempFromDate(e.target.value)}
                  required
                />
              </div>
              <div className="el-form-group">
                <label htmlFor="elToDate">To Date:</label>
                <input
                  id="elToDate"
                  type="date"
                  value={tempToDate}
                  onChange={(e) => setTempToDate(e.target.value)}
                  required
                />
              </div>
              <div className="el-modal-actions">
                <button
                  type="button"
                  className="el-btn-cancel"
                  onClick={() => setIsModalOpen(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="el-btn-save">
                  Apply Dates
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExperienceLetter;